<template>
  <div v-if="isTestDriveFromHomeEnabled">
    <div
      class="otb-test-drive-from-home-information"
      :class="{ 'otb-test-drive-from-home-information--bordered': isInfo }"
    >
      <div
        class="otb-test-drive-from-home-information__content"
        :class="{
          'otb-test-drive-from-home-information__content--desktop':
            !isSmallScreen,
        }"
      >
        <div class="otb-test-drive-from-home-information__content__container">
          <wb-checkbox-control v-if="!isInfo">
            <label>
              <input
                type="checkbox"
                name="test-drive-from-home"
                v-model="isTestDriveFromHomeChecked"
              />
              <span>&#8203;</span>
            </label>
          </wb-checkbox-control>
          <div
            v-else
            class="otb-test-drive-from-home-information__content__container__icon"
          >
            <wb-icon
              name="bds/info-circle/16"
              class="otb-test-drive-from-home-information__content__container__icon__icon"
            ></wb-icon>
          </div>

          <div
            class="otb-test-drive-from-home-information__content__container__text"
          >
            <div
              class="otb-test-drive-from-home-information__content__container__text__title"
            >
              <span class="wb-type-copy-strong">{{
                $t('tdfh.header.title')
              }}</span>
              <wb-tag v-if="!isInfo" variant="primary">{{
                $t('tdfh.header.badge')
              }}</wb-tag>
            </div>
            <span
              class="otb-test-drive-from-home-information__content__container__text__description wb-type-copy-secondary"
            >
              {{ $t('tdfh.description.text') }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="shouldShowDetails">
        <div
          v-if="isSmallScreen || isInfo"
          class="otb-test-drive-from-home-information__divider"
        />
        <div
          class="otb-test-drive-from-home-information__footer"
          :class="{
            'otb-test-drive-from-home-information__footer__desktop__not-info':
              !isSmallScreen && !isInfo,
            'otb-test-drive-from-home-information__footer__desktop__info':
              !isSmallScreen && isInfo,
          }"
        >
          <button
            type="button"
            class="wb-button wb-button--tertiary wb-button--medium"
            @click="handleInformationModalOpen"
          >
            {{ $t('tdfm.footer.cta') }}
          </button>
        </div>
      </div>
    </div>

    <TestDriveFromHomeInformationModal
      v-if="shouldShowDetails"
      :isInfo="isInfo"
      :isOpen="isInformationModalOpen"
      @event:close="handleInformationModalClose"
      @event:confirmation="handleInformationModalConfirmation"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed } from 'vue';

import { i18n } from '../../services/translation.service';

import { useStore } from '../../composables/store.composable';
import { useMediaQuery } from '../../composables/media-query.composable';

import { TestDriveFromHomeInformationModal } from './index';
import {
  TRACKING_EVENT_ACTION,
  TRACKING_EVENT_CATEGORY,
  TRACKING_EVENT_LABEL,
  TRACKING_EVENT_NAME,
} from '../../models/constants';
import { EventTrackingInfo } from '../../models/entities/tracking.model';
import TrackingService from '../../services/tracking.service';

export default defineComponent({
  i18n,
  name: 'otb-test-drive-from-home-information',
  components: {
    TestDriveFromHomeInformationModal,
  },
  props: {
    isInfo: {
      type: Boolean,
      default: false,
    },
    shouldHide: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    // hooks
    const { getter, commit } = useStore();
    const { isSmallScreen } = useMediaQuery();

    // refs
    const shouldShowDetails = ref(true);
    const isTestDriveFromHomeChecked = ref(
      getter?.isTestDriveFromHomeEnabled ?? false
    );
    const isInformationModalOpen = ref(false);

    // computed
    const isTestDriveFromHomeEnabled = computed(() => {
      // always show if it is info and it was checked previously
      if (props.isInfo) return getter?.isTestDriveFromHomeEnabled;
      // never show if is not info or is not in define-location page
      if (props.shouldHide) return false;
      // anything else, shows if should be displayed (!isInfo) check OTBCC configuration
      return getter?.isPickMeUpFromHomeActive;
    });

    const trackTestDriveFromHomeSelection = (
      action: TRACKING_EVENT_ACTION,
      name?: TRACKING_EVENT_NAME
    ) => {
      const label = TRACKING_EVENT_LABEL.TEST_DRIVE_FROM_HOME;
      if (TrackingService.hasEventBeenCalled(action, label)) return;
      const event: EventTrackingInfo = {
        category: TRACKING_EVENT_CATEGORY.SEARCH,
        name,
        label,
        action,
      };
      TrackingService.trackEvent(event);
    };

    // watchers
    watch(isTestDriveFromHomeChecked, (isEnabled) => {
      commit('isTestDriveFromHome', isEnabled);
      trackTestDriveFromHomeSelection(
        TRACKING_EVENT_ACTION.FIND_TEST_DRIVE,
        TRACKING_EVENT_NAME.FEATURE
      );
    });

    // handlers
    const handleInformationModalOpen = () =>
      (isInformationModalOpen.value = true);
    const handleInformationModalClose = () =>
      (isInformationModalOpen.value = false);
    const handleInformationModalConfirmation = () => {
      isTestDriveFromHomeChecked.value = true;
      handleInformationModalClose();
    };

    return {
      // hooks
      isSmallScreen,
      // computed
      isTestDriveFromHomeEnabled,
      // refs
      shouldShowDetails,
      isTestDriveFromHomeChecked,
      isInformationModalOpen,
      // handlers
      handleInformationModalOpen,
      handleInformationModalClose,
      handleInformationModalConfirmation,
      // tracking
      trackTestDriveFromHomeSelection,
    };
  },
});
</script>
<style lang="scss" src="./test-drive-from-home-information.scss"></style>
